/* eslint-disable max-lines-per-function */
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { StyledHorizontalScrollerContainer, StyledHorizontalScrollerItemsContainer } from './HorizontalScroller.styled';
import { HorizontalScrollerNavigation } from './HorizontalScrollerNavigation';
import { preventEdgeScrolling } from './HorizontalScrollerNavigation.utils';

const SCROLL_THRESHOLD = 40;

type HoriontalScrollerProps = {
   readonly children: ReactNode;
};

export const HorizontalScroller = (props: HoriontalScrollerProps): JSX.Element => {
   const itemsContainerRef = useRef<HTMLDivElement>(null);
   const itemWidthRef = useRef<number>(0);

   const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
   const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

   const updateScrollButtons = useCallback(() => {
      if (!itemsContainerRef.current) {
         return;
      }
      const { scrollLeft, scrollWidth, clientWidth } = itemsContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
   }, []);

   const onMouseWheel = useCallback((event: WheelEvent): void => {
      const itemsContainerEl = itemsContainerRef?.current;
      if (!itemsContainerEl) {
         return;
      }

      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
         event.preventDefault();
         itemsContainerEl.scrollBy({ left: 2 * event.deltaY, behavior: 'smooth' });
         return;
      }

      preventEdgeScrolling(itemsContainerEl, event);
   }, []);

   useLayoutEffect(() => {
      const itemsContainerEl = itemsContainerRef.current;

      const itemsContainerGap = itemsContainerEl ? parseInt(getComputedStyle(itemsContainerEl).gap, 10) : 0;
      const calculatedItemWidth =
         (itemsContainerEl?.firstElementChild?.getBoundingClientRect?.()?.width ?? 0) + itemsContainerGap;
      itemWidthRef.current = calculatedItemWidth;
      updateScrollButtons();

      const resizeObserver = new ResizeObserver(updateScrollButtons);
      const mutationObserver = new MutationObserver(updateScrollButtons);

      if (itemsContainerEl) {
         resizeObserver.observe(itemsContainerEl);
         mutationObserver.observe(itemsContainerEl, { childList: true });
      }

      itemsContainerEl?.addEventListener('scrollend', updateScrollButtons);
      itemsContainerEl?.addEventListener('wheel', onMouseWheel, { passive: false });

      return () => {
         resizeObserver.disconnect();
         mutationObserver.disconnect();
         itemsContainerEl?.removeEventListener('scrollend', updateScrollButtons);
         itemsContainerEl?.removeEventListener('wheel', onMouseWheel);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const scrollLeft = useCallback(() => {
      const itemsContainerEl = itemsContainerRef?.current;
      if (!itemsContainerEl) {
         return;
      }

      const { scrollLeft, clientWidth } = itemsContainerEl;
      const itemWidth = itemWidthRef.current;
      if (clientWidth <= 0) {
         return;
      }

      const remainder = scrollLeft % itemWidth;
      const scrollBy = remainder + SCROLL_THRESHOLD;
      itemsContainerEl.scrollBy({
         left: -scrollBy,
         behavior: 'smooth',
      });
      updateScrollButtons();
   }, [updateScrollButtons]);

   const scrollRight = useCallback(() => {
      const itemsContainerEl = itemsContainerRef?.current;
      if (!itemsContainerEl) {
         return;
      }

      const { scrollLeft, clientWidth } = itemsContainerEl;
      const itemWidth = itemWidthRef.current;
      if (clientWidth <= 0) {
         return;
      }

      const itemsVisible = Math.floor(clientWidth / itemWidth);
      const snapWidth = (itemsVisible + 1) * itemWidth - clientWidth + SCROLL_THRESHOLD;
      const remainder = scrollLeft % itemWidth;

      let scrollBy = 0;
      if (Math.floor(snapWidth) === Math.floor(remainder)) {
         scrollBy = itemWidth;
      } else if (remainder > snapWidth) {
         scrollBy = itemWidth - remainder + snapWidth;
      } else {
         scrollBy = snapWidth - remainder;
      }

      itemsContainerEl.scrollBy({
         left: scrollBy,
         behavior: 'smooth',
      });
      updateScrollButtons();
   }, [updateScrollButtons]);

   return (
      <StyledHorizontalScrollerContainer>
         <HorizontalScrollerNavigation direction={'left'} onClick={scrollLeft} visible={canScrollLeft} />
         <StyledHorizontalScrollerItemsContainer ref={itemsContainerRef}>
            {props.children}
         </StyledHorizontalScrollerItemsContainer>
         <HorizontalScrollerNavigation direction={'right'} onClick={scrollRight} visible={canScrollRight} />
      </StyledHorizontalScrollerContainer>
   );
};
